import React from 'react';
import { NavLink } from 'react-router-dom';

import { Box, Collapse, useMediaQuery } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip';
import PropTypes from 'prop-types';

import styles from '../sidebar.module.css';

import { ReactComponent as ChevronRight } from 'asset/IconsPack/at_chevron-right-filled.svg';

import RailFlyout from './RailFlyout';
import SettingsSubNav from './SettingsSubNav';
import ThemesSubNav from './ThemesSubNav';
import WorkflowsSubNav from './WorkflowsSubNav';

const subNavs = { workflows: WorkflowsSubNav, themes: ThemesSubNav, settings: SettingsSubNav };

function NavItem({ item, isActive, isOpen, onToggle, collapsed, adminType, user }) {
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const hasMenu = item.hasSubPages && !collapsed;
    const subNavId = `${item.id}-sub-pages`;
    const SubNav = subNavs[item.id];
    const collapsedLabel = item.isNew ? `${item.label}, new` : item.label;

    const content = (
        <>
            <span className={styles.navIcon}>{item.icon}</span>
            {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
            {item.isNew && collapsed && <span className={styles.newDot} aria-hidden="true" />}
            {item.isNew && !collapsed && (
                <>
                    <span className={styles.newFlag} aria-hidden="true">
                        New
                    </span>
                    <Box component="span" sx={visuallyHidden}>
                        (new)
                    </Box>
                </>
            )}
            {hasMenu && (
                <ChevronRight
                    className={clsx(styles.navChevron, isOpen && styles.navChevronOpen)}
                    aria-hidden="true"
                />
            )}
        </>
    );

    if (item.hasSubPages && collapsed) {
        return (
            <RailFlyout item={item} isActive={isActive}>
                <SubNav adminType={adminType} user={user} />
            </RailFlyout>
        );
    }

    if (hasMenu) {
        return (
            <Box className={styles.navItemWithMenu} data-tour={item.id}>
                <button
                    type="button"
                    className={clsx(
                        styles.navItem,
                        styles.navItemButton,
                        // While open, the sub-page carries the highlight instead.
                        isActive && !isOpen && styles.navItemActive
                    )}
                    onClick={() => onToggle(item.id)}
                    aria-expanded={isOpen}
                    aria-controls={subNavId}
                >
                    {content}
                </button>
                <Collapse in={isOpen} timeout={reduceMotion ? 0 : 200} id={subNavId}>
                    <SubNav adminType={adminType} user={user} />
                </Collapse>
            </Box>
        );
    }

    return (
        <Tooltip
            title={collapsed ? item.label : ''}
            placement="right"
            classes={{ tooltip: styles.collapseTooltip }}
        >
            <NavLink
                data-tour={item.id}
                to={item.path}
                isActive={() => isActive}
                className={clsx(styles.navItem, collapsed && styles.navItemCollapsed)}
                activeClassName={styles.navItemActive}
                aria-label={collapsed ? collapsedLabel : undefined}
            >
                {content}
            </NavLink>
        </Tooltip>
    );
}

NavItem.propTypes = {
    item: PropTypes.object.isRequired,
    isActive: PropTypes.bool,
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func,
    collapsed: PropTypes.bool,
    adminType: PropTypes.string,
    user: PropTypes.object,
};

export default NavItem;
